<template>
  <b-dropdown
    class="multi-select-filter"
    toggle-class="multi-select-filter__toggle"
    menu-class="multi-select-filter__menu"
    variant="outline-secondary"
    size="sm"
    :data-cy="dataCy"
    @hidden="searchText = ''"
  >
    <template #button-content>
      <span class="multi-select-filter__label">{{ label }}</span>
      <b-badge
        v-if="value.length > 0"
        variant="primary"
        class="ml-1"
        pill
        data-cy="multi-select-filter-count"
      >
        {{ value.length }}
      </b-badge>
    </template>

    <!-- A form, not items: a click on a checkbox keeps the menu open for the next one -->
    <b-dropdown-form form-class="multi-select-filter__form">
      <b-form-input
        v-model="searchText"
        size="sm"
        type="search"
        :placeholder="`Search ${label.toLowerCase()}`"
        autocomplete="off"
        data-cy="multi-select-filter-search"
      />

      <div class="d-flex align-items-center justify-content-between my-1">
        <small class="text-muted">{{ value.length }} selected</small>

        <b-button
          variant="link"
          size="sm"
          class="p-0"
          :disabled="value.length === 0"
          data-cy="multi-select-filter-clear"
          @click="clear"
        >
          Clear
        </b-button>
      </div>

      <div class="multi-select-filter__list">
        <!-- b-form-checkbox puts attributes on its input, so the data-cy sits on a wrapper -->
        <div
          v-for="option in filteredOptions"
          :key="option.id"
          class="multi-select-filter__option"
          data-cy="multi-select-filter-option"
        >
          <b-form-checkbox
            :checked="selectedIds.has(option.id)"
            @change="toggle(option.id, $event)"
          >
            {{ option.name || option.id }}
          </b-form-checkbox>
        </div>

        <small
          v-if="filteredOptions.length === 0"
          class="text-muted"
          data-cy="multi-select-filter-empty"
        >
          {{ options.length === 0 ? 'No values in this period' : 'No matches' }}
        </small>
      </div>
    </b-dropdown-form>
  </b-dropdown>
</template>

<script>
import { BBadge, BButton, BDropdown, BDropdownForm, BFormCheckbox, BFormInput } from 'bootstrap-vue'

/**
 * A dropdown of checkboxes for one Call Logs filter. `v-model` is the list of selected ids; the menu shows
 * the options' names, a text filter over them, and Clear. The badge on the button counts the selection.
 */
export default {
  name: 'MultiSelectFilter',

  components: {
    BBadge,
    BButton,
    BDropdown,
    BDropdownForm,
    BFormCheckbox,
    BFormInput
  },

  props: {
    // [{ id, name }]
    options: {
      type: Array,
      default: () => []
    },

    // Selected ids
    value: {
      type: Array,
      default: () => []
    },

    label: {
      type: String,
      required: true
    },

    dataCy: {
      type: String,
      default: 'multi-select-filter'
    }
  },

  data() {
    return {
      searchText: ''
    }
  },

  computed: {
    selectedIds() {
      return new Set(this.value)
    },

    filteredOptions() {
      const search = this.searchText.trim().toLowerCase()

      if (!search) {
        return this.options
      }

      return this.options.filter(option => String(option.name || option.id).toLowerCase().includes(search))
    }
  },

  methods: {
    toggle(id, checked) {
      const others = this.value.filter(selectedId => selectedId !== id)

      this.$emit('input', checked ? [...others, id] : others)
    },

    clear() {
      this.$emit('input', [])
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .multi-select-filter__menu {
  min-width: 16rem;
}

::v-deep .multi-select-filter__form {
  padding: 0.5rem 0.75rem;
}

.multi-select-filter__list {
  max-height: 16rem;
  overflow-y: auto;
}

.multi-select-filter__option {
  white-space: nowrap;
}
</style>
